import Vue from 'vue'
import axios from '../config/api'
import { useAxiosErrorHandler } from '@/composables/useAxiosErrorHandler'
import MERCURE_TOPIC from '../config/mercure'
const campaignType = process.env.VUE_APP_CAMPAIGN_TYPE ?? 'BNN'

const state = () => ({
  data: null,
  hasData: false,
  isError: false,
  errorMessage: null,
  isLoading: false,
  isVerified: false,
  eventSources: {},
  qrStatus: null,
})

// getters
const getters = {
  isLoading: state => state.isLoading,
}

// actions
const actions = {
  async getStockLive({ commit }, { slug, refetch }) {
    if (!slug) {
      return
    }

    try {
      if (refetch) {
        commit('setStockData')
      }
      const response = await axios.get(
        `/v2/products/live/stock/${campaignType}/${slug}`,
      )

      commit('setStockDataSuccess', response.data.data)
    } catch {
      commit('setStockDataError')
    }
  },

  async staffVerify({ commit }, payload) {
    commit('staffVerifyRequest')

    const accessToken = Vue.prototype.$cookies.get('token')

    if (!accessToken) {
      Vue.$toast.error('กรุณาเข้าสู่ระบบ', {
        duration: 5000,
        position: 'top',
      })
      return
    }

    await axios
      .post('/staff/verify', payload, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
      .then(response => {
        commit('staffVerifyRequestSuccess', response)
        Vue.prototype.$cookies.set('sc', payload.staff_code)
        Vue.prototype.$cookies.set('bc', payload.branch_code)
      })
      .catch(error => {
        const message = useAxiosErrorHandler().handleError(error)
        commit('staffVerifyRequestFailure', message)
        Vue.prototype.$cookies.remove('sc')
        Vue.prototype.$cookies.remove('bc')
      })
  },

  async generateProductLink(context, payload) {
    const accessToken = Vue.prototype.$cookies.get('token')

    if (!accessToken) {
      Vue.$toast.error('กรุณาเข้าสู่ระบบ', {
        duration: 5000,
        position: 'top',
      })
      return ''
    }

    return await axios
      .post('staff/generate-product-link', payload, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
      .then(response => {
        return {
          url: `${response.data.data?.url}&openExternalBrowser=1`,
          productQuotaTimeDate:
            response.data.data?.product_quota_time_date,
          code: response.data.data?.code,
        }
      })
      .catch(error => {
        useAxiosErrorHandler().handleError(error, {
          slug: payload?.slug,
        })
        return ''
      })
  },

  resetVerify({ commit }) {
    commit('staffVerifyRequest')
  },

  /* Subscribe to QR status updates with Mercure SSE */
  subscribeQrStatus({ commit, state }, code) {
    // Check if already subscribed and connecting
    if (
      code &&
      state.eventSources[code] &&
      state.eventSources[code].readyState === 0
    ) {
      return
    }

    commit('resetQrStatus')

    const hubUrl = process.env.VUE_APP_HUB_MERCURE_URL
    const env =
      process.env.NODE_ENV !== 'production' ? 'dev' : 'production'
    const topic = `${env}.${MERCURE_TOPIC.QR_STATUS}.${code}`

    const url = `${hubUrl}?topic=${topic}`

    const es = new EventSource(url)

    state.eventSources[code] = es

    state.eventSources[code].onmessage = event => {
      const data = JSON.parse(event.data)

      if (data.event === 'stockcard.link.opened') {
        commit('setQrStatusSuccess', 'activated')
      }
    }
  },
  unsubscribeQrStatus({ state }, code) {
    if (code && state.eventSources[code]) {
      state.eventSources[code].close()
      delete state.eventSources[code]
    }
  },
}

// mutations
const mutations = {
  setHasData(state, value) {
    state.hasData = value
  },
  setStockData(state) {
    state.isLoading = !state?.data
    state.data = state?.data
    state.hasData = state?.data || false
    state.isError = false
  },
  setStockDataSuccess(state, payload) {
    state.hasData = true
    state.isLoading = false
    state.data = payload
    state.isError = false
  },
  setStockDataError(state) {
    state.isLoading = false
    state.isError = true
    state.hasData = false
  },
  staffVerifyRequest(state) {
    state.isVerified = false
    state.errorMessage = null
  },
  staffVerifyRequestSuccess(state) {
    state.isVerified = true
    state.errorMessage = null
  },
  staffVerifyRequestFailure(state, payload) {
    state.isVerified = false
    state.errorMessage = payload
  },
  setQrStatusSuccess(state, payload) {
    state.qrStatus = payload
  },
  resetQrStatus(state) {
    state.qrStatus = null
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
